import React, { useEffect, useState } from "react";
import { dateTimeLong } from "@/lib/dates";
import { useEscape } from "@/hooks/useEscape";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, X, Copy, Check, ShieldAlert } from "lucide-react";

const ALL_SCOPES = [
  { id: "post_message", label: "post_message", hint: "trimite anunțuri de livrare" },
  { id: "patch_ticket", label: "patch_ticket", hint: "marchează tichetele Reparație livrată" },
  { id: "read_feedback", label: "read_feedback", hint: "citește tichetele prin API" },
  { id: "reply_ticket", label: "reply_ticket", hint: "scrie feedback de agent pe tichete" },
  { id: "close_ticket", label: "close_ticket", hint: "rezolvă și închide tichete" },
];

function timeStr(iso) {
  if (!iso) return "never";
  try { return dateTimeLong(iso); }
  catch { return iso; }
}

// One-time reveal of a freshly minted token. The plaintext is gone after this closes.
function NewTokenReveal({ token, name, onClose }) {
  useEscape(true, onClose);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copierea nu a reușit — selectează textul manual"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onMouseDown={onClose} data-testid="bot-token-reveal">
      <div onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-lg bg-surface border border-line/10 shadow-xl">
        <div className="h-12 px-4 flex items-center justify-between border-b border-line/10">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-brand" />
            <h3 className="font-bold tracking-tight">Token “{name}” a fost creat</h3>
          </div>
          <button type="button" onClick={onClose} className="text-fg/50 hover:text-fg" data-testid="bot-token-reveal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-2 text-xs text-fg/70 bg-surface-2 border border-line/10 p-2.5">
            <ShieldAlert className="w-4 h-4 text-brand-2 flex-shrink-0 mt-0.5" />
            <span>Copiază acum — nu se mai poate vedea. Acest token nu mai poate fi recuperat după ce închizi această fereastră.</span>
          </div>
          <div className="flex items-center gap-2">
            <code data-testid="bot-token-value" className="flex-1 mono text-xs bg-surface-2 border border-line/10 p-2.5 break-all select-all">{token}</code>
            <button type="button" onClick={copy} data-testid="bot-token-copy" className="btn-ghost text-xs flex items-center gap-1.5 px-2.5 py-2 flex-shrink-0">
              {copied ? <><Check className="w-3.5 h-3.5 text-brand-2" /> Copiat</> : <><Copy className="w-3.5 h-3.5" /> Copiază</>}
            </button>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className="btn-primary text-sm">Gata</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateTokenModal({ onClose, onCreated }) {
  useEscape(true, onClose);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState([
    "post_message", "patch_ticket", "read_feedback", "reply_ticket",
  ]);
  const [saving, setSaving] = useState(false);

  const toggle = (id) =>
    setScopes((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { toast.error("Numele e obligatoriu"); return; }
    if (scopes.length === 0) { toast.error("Alege cel puțin un drept"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/bot/token", { name: name.trim(), scopes });
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tokenul nu a putut fi creat");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onMouseDown={onClose} data-testid="bot-token-create-modal">
      <form onSubmit={submit} onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-md bg-surface border border-line/10 shadow-xl">
        <div className="h-12 px-4 flex items-center justify-between border-b border-line/10">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-brand" />
            <h3 className="font-bold tracking-tight">Creează un token de bot</h3>
          </div>
          <button type="button" onClick={onClose} className="text-fg/50 hover:text-fg" data-testid="bot-token-create-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="label-tiny block mb-1.5">Nume *</label>
            <input
              data-testid="bot-token-name-input"
              className="input-flat mono"
              placeholder="qedara-start-deploy"
              value={name} onChange={(e) => setName(e.target.value)}
              maxLength={80} autoFocus
            />
          </div>
          <div>
            <label className="label-tiny block mb-1.5">Drepturi</label>
            <div className="space-y-1.5">
              {ALL_SCOPES.map((s) => (
                <label key={s.id} className="flex items-center gap-2.5 bg-surface-2 border border-line/10 px-3 py-2 cursor-pointer hover:border-line/30 transition-colors">
                  <input
                    type="checkbox"
                    data-testid={`bot-token-scope-${s.id}`}
                    checked={scopes.includes(s.id)}
                    onChange={() => toggle(s.id)}
                    className="accent-brand"
                  />
                  <span className="mono text-sm text-fg">{s.label}</span>
                  <span className="text-[11px] text-fg/40 ml-auto">{s.hint}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="text-sm text-fg/50 hover:text-fg px-3 py-2">Anulează</button>
            <button type="submit" disabled={saving} data-testid="bot-token-create-submit" className="btn-primary text-sm flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> {saving ? "Se creează…" : "Creează tokenul"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function BotTokensSettings() {
  const { ask, confirmProps } = useConfirm();
  const [tokens, setTokens] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [allowed, setAllowed] = useState(true);   // owner-only feature
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState(null);      // { token, name } shown once

  const load = async () => {
    try {
      const r = await api.get("/bot/tokens");
      setTokens(r.data || []);
      setAllowed(true);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 503) setAllowed(false);
    } finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, []);

  const onCreated = (data) => {
    setCreating(false);
    setReveal({ token: data.token, name: data.name });
    load();
  };

  const revoke = (t) => {
    ask({
      title: `Revoci token-ul „${t.name}"?`, destructive: true, confirmLabel: "Revocă",
      description: "Devine invalid imediat, pentru orice îl folosește. Nu se poate anula.",
      testId: "confirm-revoke-token", onConfirm: () => doRevoke(t),
    });
  };

  const doRevoke = async (t) => {
    try {
      await api.delete(`/bot/token/${t.id}`);
      toast.success("Token revocat");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revocarea nu a reușit");
    }
  };

  // Hide the whole section for non-owners (and when the feature isn't configured).
  if (loaded && !allowed) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="api-integrations-section">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">API și integrări</h3>
        <span className="label-tiny ml-2">// TOKENURI DE BOT</span>
        <button
          type="button"
          data-testid="bot-token-create-button"
          onClick={() => setCreating(true)}
          className="ml-auto btn-primary text-xs flex items-center gap-1.5 px-2.5 py-1"
        >
          <Plus className="w-3.5 h-3.5" /> Creează tokenul
        </button>
      </div>
      <div className="p-5">
        <p className="text-xs text-fg/50 leading-relaxed mb-4">
          Tokenurile de bot lasă scripturile de livrare să scrie în <code className="mono text-brand">#deployments</code> și marchează tichetele <span className="mono">Reparație livrată</span> prin <code className="mono text-brand">/api/deployments/notify</code>. Un token se arată o singură dată, la creare — păstrează-l în siguranță.
        </p>

        {!loaded ? (
          <div className="py-8 text-center label-tiny text-brand">se încarcă tokenurile<span className="cursor-blink">_</span></div>
        ) : tokens.length === 0 ? (
          <div className="py-6 text-center text-sm text-fg/40">
            <p className="label-tiny mb-1">// NICIUN TOKEN ÎNCĂ</p>
            <p>Creează unul ca să legi un script de livrare.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="bot-token-list">
            {tokens.map((t) => (
              <div key={t.id} data-testid={`bot-token-row-${t.id}`} className={`bg-surface-2 border border-line/10 p-3 flex items-start gap-3 ${t.revoked ? "opacity-50" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="mono text-sm font-semibold text-fg truncate">{t.name}</span>
                    {t.revoked && <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm bg-fg/10 text-fg/60">revocat</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    {(t.scopes || []).map((s) => (
                      <span key={s} className="text-[10px] mono px-1.5 py-0.5 rounded-sm bg-brand/15 text-brand">{s}</span>
                    ))}
                  </div>
                  <p className="text-[11px] text-fg/40 mono mt-1.5">
                    creat {timeStr(t.created_at)} · folosit ultima dată {timeStr(t.last_used_at)}
                  </p>
                </div>
                {!t.revoked && (
                  <button
                    type="button"
                    data-testid={`bot-token-revoke-${t.id}`}
                    onClick={() => revoke(t)}
                    title="Revocă tokenul"
                    aria-label={`Revoke token ${t.name}`}
                    className="text-fg/40 hover:text-destructive flex-shrink-0 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {creating && <CreateTokenModal onClose={() => setCreating(false)} onCreated={onCreated} />}
      {reveal && <NewTokenReveal token={reveal.token} name={reveal.name} onClose={() => setReveal(null)} />}
      <ConfirmDialog {...confirmProps} />
    </section>
  );
}
